import { Breadcrumbs, Button, Div, NavigationHeader } from '@driftt/tide-core'
import { BreadcrumbsHeadingProps } from '@driftt/tide-core'
import { Subnav } from '@driftt/tide-subnav'
import { showTransientNotification } from '@driftt/traffic-control'
import { pathOr } from 'ramda'
import React, { useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'

import { saveGlobalPriorities, savePriorities } from 'api/playbooks'
import PlaybookPriorityTable, {
  PriorityPayload,
} from 'components/PlaybookPriorityTable'
import useWorkspacesGeoTargeting, {
  Workspace,
} from 'hooks/useWorkspacesGeoTargeting'
import { getOrgWorkspaces } from 'modules/workspace/selectors'
import {
  getWorkspacesWithChatPlaybooksWrite,
  userHasAnyPlaybookAccess,
  userHasPlaybookPriorityAccess,
} from 'utils/access'
import { getSubNavNodes } from 'utils/getSubNavNodes'

import styles from './styles.module.css'

const breadcrumbsTemplate = (
  typeDisplayName: string,
  typePathName: string
): BreadcrumbsHeadingProps[] => {
  return [
    {
      title: 'Chat',
      link: '/playbooks',
    },
    {
      title: typeDisplayName,
      link: `/playbooks/list/${typePathName}`,
    },
    {
      title: `Set priority`,
      link: '/',
      isActive: true,
    },
  ]
}

const typeToBreadcrumbs = {
  chat: breadcrumbsTemplate('Chat Playbooks', 'chat'),
  meetings: breadcrumbsTemplate('Meeting', 'meetings'),
}

const generateWorkspaceOptions = (workspaces: Workspace[]) =>
  workspaces
    .filter((workspace) => workspace && workspace.id && workspace.name)
    .map((workspace) => ({
      label: workspace.name,
      value: workspace.id,
    }))

const PlaybookPriorityLayout = ({ history, match }: RouteComponentProps) => {
  const [priorityPayload, setPriorityPayload] = useState<PriorityPayload>({
    idPriorityList: [],
    playbookTypes: { ABM: [], nonSpecial: [], outbound: [] },
  })
  const [hasPriorityChanged, setHasPriorityChanged] = useState(false)
  const dispatch = useDispatch()

  const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()
  const [sidebarIsOpen, setSidebarIsOpen] = useState(true)
  const SUB_NAV_NODES = getSubNavNodes()

  const typeInPath = pathOr('chat', ['params', 'type'], match)
  const type =
    (Object.keys(typeToBreadcrumbs) as (keyof typeof typeToBreadcrumbs)[]).find(
      (t) => t === typeInPath.toLowerCase()
    ) || 'chat'
  const headings = typeToBreadcrumbs[type]

  const { orgHasWorkspaces } = useWorkspacesGeoTargeting()
  const orgWorkspaces = useSelector(getOrgWorkspaces)
  const writableWorkspaces = getWorkspacesWithChatPlaybooksWrite().map(
    (id) => orgWorkspaces[id as keyof typeof orgWorkspaces]
  )
  const workspacesOptions = useMemo(
    () =>
      orgHasWorkspaces ? generateWorkspaceOptions(writableWorkspaces) : [],
    [orgHasWorkspaces, writableWorkspaces]
  )
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>(() => {
    return workspacesOptions[0]?.value || ''
  })

  const { mutate: savePrioritiesMutation, isLoading: isSaving } = useMutation({
    mutationKey: 'save-playbooks-priority',
    mutationFn: (params: { idPriorityList: number[]; type: string }) =>
      !userHasPlaybookPriorityAccess() && selectedWorkspace
        ? savePriorities(
            selectedWorkspace,
            { priorityList: params.idPriorityList },
            {
              type: params.type,
            }
          )
        : saveGlobalPriorities(params.idPriorityList, { type: params.type }),
    onSuccess: () => {
      history.push('/playbooks')

      dispatch(
        showTransientNotification({
          message: 'Playbook priority has been successfully updated!',
          type: 'success',
        })
      )
    },
  })

  const onSave = () => {
    savePrioritiesMutation({
      idPriorityList: priorityPayload.idPriorityList,
      type,
    })
  }

  return (
    <>
      {hasAnyPlaybookAccess && (
        <Subnav
          title="Chatbot"
          nodes={SUB_NAV_NODES}
          open={sidebarIsOpen}
          onToggle={() => setSidebarIsOpen(!sidebarIsOpen)}
        />
      )}
      <Div className={styles.playbookPriorityPageContainer}>
        <NavigationHeader
          title="Set priority"
          secondary={<Breadcrumbs headings={headings} />}
        >
          <Div>
            <Button
              type="tertiary"
              className={styles.playbookPriorityCancelButton}
              onClick={() => history.goBack()}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={onSave}
              loading={isSaving}
              disabled={!hasPriorityChanged}
            >
              Save changes
            </Button>
          </Div>
        </NavigationHeader>
        <Div className={styles.playbookPriorityTableContainer}>
          <PlaybookPriorityTable
            priorityPayload={priorityPayload}
            setPriorityPayload={setPriorityPayload}
            setHasPriorityChanged={setHasPriorityChanged}
            type={type}
            allWorkspaces={workspacesOptions}
            selectedWorkspace={selectedWorkspace}
            showWorkspacesColumn={orgHasWorkspaces}
            onSelectWorkspace={setSelectedWorkspace}
          />
        </Div>
      </Div>
    </>
  )
}

export default PlaybookPriorityLayout
